import React, { useEffect, useState } from "react";
import { Button, Modal, Form, Row, Col } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import userService from "../services/userService";
import dateService from "../services/dateService";
import { toast } from "react-toastify";
import { BsPlusLg, BsPencil, BsTrash, BsPerson } from 'react-icons/bs';
import { FaUser } from 'react-icons/fa';
import ResponsiveTable from "../components/ResponsiveTable";
import { API_BASE_URL } from "../configs/apiConfig"; // Import API base URL
import ProfileImage from "./ProfileImage";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    birthDate: null,
    phoneNumber: "",
    mobileNumber: "",
    address: "",
    postCode: "",
    personnelCode: "",
    userType: "1",
    isActive: true,
    imageUrl: "", // Existing user image
    profileImageFile: null, // New image file
  });

  const userTypes = [
    { value: "0", label: "Administrator" },
    { value: "1", label: "Normal User" },
    { value: "2", label: "Manager" },
   
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await userService.getAllUsers();
      setUsers(data);
    } catch (error) {
      toast.error("Failed to fetch users.");
    }
  };

  const handleShowModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        ...user,
        password: "",
        birthDate: user.birthDate ? new Date(user.birthDate) : null,
        profileImageFile: null // Reset file input
      });
      setSelectedImage(null);// Reset preview
    } else {
      setEditingUser(null);
      setFormData({
        username: "",
        email: "",
        password: "",
        firstName: "",
        lastName: "",
        birthDate: null,
        phoneNumber: "",
        mobileNumber: "",
        address: "",
        postCode: "",
        personnelCode: "",
        userType: "1",
        isActive: true,
        imageUrl: "",
        profileImageFile: null
      });
      setSelectedImage(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedImage(null);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(URL.createObjectURL(file)); // Preview new image
      setFormData(prev => ({ ...prev, profileImageFile: file }));  // Store file
    }
  };
  

  const isValidPassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validateForm = () => {
    let isValid = true;
    
    // Only validate username and email as required fields
    if (!formData.username?.trim()) {
      toast.error("Username is required.");
      isValid = false;
    }

    if (!formData.email?.trim()) {
      toast.error("Email is required.");
      isValid = false;
    } else if (!isValidEmail(formData.email)) {
      toast.error("Invalid email format.");
      isValid = false;
    }

    // Only validate password if it's provided (for both new and existing users)
    if (formData.password && !isValidPassword(formData.password)) {
      toast.error("If provided, password must be at least 8 characters, include uppercase, lowercase, number, and special character.");
      isValid = false;
    }

    return isValid;
  };

  const handleSaveUser = async () => {
    if (!validateForm()) return;
  
    try {
      const formDataToSend = new FormData();
      
      // Required fields
      formDataToSend.append('username', formData.username?.trim() ?? '');
      formDataToSend.append('email', formData.email?.trim() ?? '');
      formDataToSend.append('userType', formData.userType);
      formDataToSend.append('isActive', formData.isActive);

      // Optional fields - send null if cleared
      const appendField = (fieldName, value) => {
        if (value === null || value === undefined || value === '') {
          formDataToSend.append(fieldName, '');
        } else {
          formDataToSend.append(fieldName, value.toString().trim());
        }
      };

      // For optional fields, we need to explicitly send them even if empty
      appendField('firstName', formData.firstName);
      appendField('lastName', formData.lastName);
      appendField('phoneNumber', formData.phoneNumber);
      appendField('mobileNumber', formData.mobileNumber);
      appendField('address', formData.address);
      appendField('postCode', formData.postCode);
      appendField('personnelCode', formData.personnelCode);
      
      // Handle birthDate separately
      if (formData.birthDate) {
        formDataToSend.append('birthDate', formData.birthDate.toISOString());
      } else {
        formDataToSend.append('birthDate', '');
      }
      
      // Password only sent if provided
      if (formData.password) {
        formDataToSend.append('password', formData.password);
      }
      
      // Profile image
      if (formData.profileImageFile instanceof File) {
        formDataToSend.append("profileImage", formData.profileImageFile);
      }

      if (editingUser) {
        await userService.updateUser(editingUser.id, formDataToSend);
        toast.success("User updated successfully!");
      } else {
        await userService.addUser(formDataToSend);
        toast.success("User added successfully!");
      }
  
      fetchUsers();
      handleCloseModal();
    } catch (error) {
      console.error("Error:", error);
      
      // Check for specific error messages
      const errorResponse = error.response?.data || '';
      
      if (typeof errorResponse === 'string' && errorResponse.includes('Email is already in use')) {
        toast.error("Email is already in use. Please use a different email address.");
      } else if (typeof errorResponse === 'string' && errorResponse.includes('Username is already in use')) {
        toast.error("Username is already in use. Please choose a different username.");
      } else if (typeof errorResponse === 'string' && errorResponse.includes('Personnel code is already in use')) {
        toast.error("Personnel code is already in use. Please use a different code.");
      } else {
        // General error handling
        const errorMessage = error.response?.data?.message || 
                           error.response?.data?.title ||    
                           error.message ||                  
                           "Operation failed.";              
        
        const cleanErrorMessage = errorMessage.replace(/<[^>]*>/g, '');
        toast.error(cleanErrorMessage);
      }
    }
  };
  

  const handleDeleteUser = async (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await userService.deleteUser(id);
        toast.success("User deleted successfully!");
        fetchUsers();
      } catch (error) {
        toast.error("Deletion failed.");
      }
    }
  };

  const columns = [
    { 
      label: "Profile", 
      key: "imageUrl", 
      render: (value) => value ? 
        <ProfileImage imageUrl={value} width={40} height={40} /> : 
        <BsPerson size={24} />
    },
    { label: "Username", key: "username" },
    { label: "Personnel Code", key: "personnelCode" },
    { label: "Full Name", key: "firstName", render: (_, user) => `${user.firstName} ${user.lastName}` },
    { label: "Email", key: "email" },
    { label: "Birth Date", key: "birthDate", render: (value) => dateService.formatDate(value) },
    { label: "Phone", key: "phoneNumber", hideOnMobile: true },
    { label: "Mobile", key: "mobileNumber", hideOnMobile: true },
    { 
      label: "User Type", 
      key: "userType", 
      render: (value) => userTypes.find(type => type.value == value)?.label || "Unknown" 
    },
    { label: "Status", key: "isActive", render: (value) => (value ? "✅ Active" : "❌ Inactive") },
  ];

  const actions = [
    { icon: <BsPencil />, variant: "outline-primary", onClick: (user) => handleShowModal(user) },
    { icon: <BsTrash />, variant: "outline-danger", onClick: (user) => handleDeleteUser(user.id) }
  ];

  return (
    <div className="container mt-2">
      <div className="mb-3">
        <Button variant="primary" onClick={() => handleShowModal()}>
          <BsPlusLg /> Add New User
        </Button>
      </div>

      <ResponsiveTable data={users} columns={columns} actions={actions} />

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingUser ? "Edit User" : "Add User"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={12} className="text-center mb-3">
                <div className="position-relative d-inline-block">
                  {selectedImage ? (
                    <img
                      src={selectedImage}
                      alt="Profile Preview"
                      style={{ width: 120, height: 120, borderRadius: "50%", objectFit: "cover" }}
                    />
                  ) : (
                    <ProfileImage 
                      imageUrl={formData.imageUrl} 
                      width={120} 
                      height={120} 
                    />
                  )}
                  <div className="mt-2">
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                      id="image-upload"
                    />
                    <label htmlFor="image-upload">
                      <Button variant="outline-primary" as="span">
                        Choose Profile Picture
                      </Button>
                    </label>
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Personnel Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.personnelCode}
                    onChange={(e) => setFormData({ ...formData, personnelCode: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Password {editingUser && "(leave blank to keep current)"}</Form.Label>
                  <Form.Control
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Birth Date</Form.Label>
                  <DatePicker
                    selected={formData.birthDate}
                    onChange={(date) => setFormData({ ...formData, birthDate: date })}
                    className="form-control"
                    dateFormat="yyyy-MM-dd"
                    wrapperClassName="w-100"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.mobileNumber}
                    onChange={(e) => setFormData({ ...formData, mobileNumber: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>User Type</Form.Label>
                  <Form.Select
                    value={formData.userType}
                    onChange={(e) => setFormData({ ...formData, userType: e.target.value })}
                  >
                    {userTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Post Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.postCode}
                    onChange={(e) => setFormData({ ...formData, postCode: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="user-status"
                    label="Active"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveUser}>Save</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserManagement;